import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Contact() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'contact' | 'recruitment'>('contact');

  // 一般お問い合わせフォーム
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    service: '',
    budget: ''
  });

  // 求人応募フォーム
  const [recruitFormData, setRecruitFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    position: '',
    experience: '',
    currentJob: '',
    motivation: '',
    selfPR: '',
    availableDate: '',
    salary: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isRecruitSubmitting, setIsRecruitSubmitting] = useState(false);
  const [recruitSubmitMessage, setRecruitSubmitMessage] = useState('');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRecruitInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setRecruitFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setSubmitMessage('必須項目をすべて入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitMessage('メッセージは500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      if (formData.company) formBody.append('company', formData.company);
      if (formData.phone) formBody.append('phone', formData.phone);
      if (formData.subject) formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);
      if (formData.service) formBody.append('service', formData.service);
      if (formData.budget) formBody.append('budget', formData.budget);

      const response = await fetch('https://readdy.ai/api/form/d6f89d2n5jm489bu5eug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitMessage('お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: '',
          service: '',
          budget: ''
        });
      } else {
        setSubmitMessage('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Contact form submit error:', error);
      setSubmitMessage('送信エラーが発生しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecruitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recruitFormData.name || !recruitFormData.email || !recruitFormData.position || !recruitFormData.motivation) {
      setRecruitSubmitMessage('必須項目をすべて入力してください。');
      return;
    }

    if (recruitFormData.motivation.length > 500) {
      setRecruitSubmitMessage('志望動機は500文字以内で入力してください。');
      return;
    }

    if (recruitFormData.selfPR.length > 500) {
      setRecruitSubmitMessage('自己PRは500文字以内で入力してください。');
      return;
    }

    setIsRecruitSubmitting(true);
    setRecruitSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', recruitFormData.name);
      formBody.append('email', recruitFormData.email);
      if (recruitFormData.phone) formBody.append('phone', recruitFormData.phone);
      if (recruitFormData.age) formBody.append('age', recruitFormData.age);
      formBody.append('position', recruitFormData.position);
      if (recruitFormData.experience) formBody.append('experience', recruitFormData.experience);
      if (recruitFormData.currentJob) formBody.append('currentJob', recruitFormData.currentJob);
      formBody.append('motivation', recruitFormData.motivation);
      if (recruitFormData.selfPR) formBody.append('selfPR', recruitFormData.selfPR);
      if (recruitFormData.availableDate) formBody.append('availableDate', recruitFormData.availableDate);
      if (recruitFormData.salary) formBody.append('salary', recruitFormData.salary);

      const response = await fetch('https://readdy.ai/api/form/d6f89d2n5jm489bu5ev0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setRecruitSubmitMessage('ご応募ありがとうございます。書類選考の上、担当者よりご連絡いたします。');
        setRecruitFormData({
          name: '',
          email: '',
          phone: '',
          age: '',
          position: '',
          experience: '',
          currentJob: '',
          motivation: '',
          selfPR: '',
          availableDate: '',
          salary: ''
        });
      } else {
        setRecruitSubmitMessage('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Recruit form submit error:', error);
      setRecruitSubmitMessage('送信エラーが発生しました。もう一度お試しください。');
    } finally {
      setIsRecruitSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-3 md:py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="text-lg sm:text-xl md:text-2xl font-bold hover:text-blue-400 transition-colors cursor-pointer"
            >
              AI Code グループ
            </button>
            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => navigate('/')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                ホーム
              </button>
              <button
                onClick={() => navigate('/media')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                メディア
              </button>
              <span className="text-blue-400">お問い合わせ</span>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 md:py-16 lg:py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">お問い合わせ</h1>
          <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 max-w-3xl mx-auto px-4">
            AIホログラム技術、パペロボ、スタジオレンタルなど、<br className="hidden sm:block" />
            お気軽にお問い合わせください
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl md:text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2">お電話</h3>
              <p className="text-xl md:text-2xl font-bold text-blue-600 mb-2">03-5990-2985</p>
              <p className="text-sm md:text-base text-gray-600">平日 10:00-18:00</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl md:text-3xl text-green-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2">メール</h3>
              <p className="text-sm md:text-lg text-green-600 mb-2 break-all px-2">holo.channel-info@es-grp.jp</p>
              <p className="text-sm md:text-base text-gray-600">24時間受付</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl md:text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2">所在地</h3>
              <p className="text-sm md:text-base text-gray-600 px-2">
                〒160-0022<br />
                東京都新宿区新宿1-34-14<br />
                第二貝塚ビル101
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section with Tabs */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4 px-2">お問い合わせフォーム</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              以下のフォームからお気軽にお問い合わせください
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-200 rounded-full p-1 flex">
              <button
                onClick={() => {
                  setActiveTab('contact');
                  setSubmitMessage('');
                  setRecruitSubmitMessage('');
                }}
                className={`px-5 md:px-8 py-2.5 md:py-3 rounded-full text-sm md:text-base font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'contact'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-mail-send-line mr-1.5"></i>
                一般お問い合わせ
              </button>
              <button
                onClick={() => {
                  setActiveTab('recruitment');
                  setSubmitMessage('');
                  setRecruitSubmitMessage('');
                }}
                className={`px-5 md:px-8 py-2.5 md:py-3 rounded-full text-sm md:text-base font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'recruitment'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-user-add-line mr-1.5"></i>
                求人応募
              </button>
            </div>
          </div>

          {/* 一般お問い合わせフォーム */}
          {activeTab === 'contact' && (
            <>
              {submitMessage && (
                <div
                  className={`mb-6 md:mb-8 p-4 rounded-lg text-sm md:text-base ${
                    submitMessage.includes('ありがとう')
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`}
                >
                  {submitMessage}
                </div>
              )}

              <div className="bg-white rounded-xl shadow-lg p-5 md:p-8">
                <form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5 md:space-y-6"
                  data-readdy-form
                >
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        placeholder="お名前を入力してください"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        placeholder="メールアドレスを入力してください"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        会社名・団体名
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        placeholder="会社名・団体名を入力してください"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        電話番号
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        placeholder="電話番号を入力してください"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                        お問い合わせ内容
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base pr-8"
                      >
                        <option value="">選択してください</option>
                        <option value="パペロボ導入">パペロボ導入について</option>
                        <option value="スタジオレンタル">スタジオレンタルについて</option>
                        <option value="ソフトウェア開発">ソフトウェア開発について</option>
                        <option value="AIソリューション">AIソリューションについて</option>
                        <option value="その他">その他のお問い合わせ</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                        ご予算
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base pr-8"
                      >
                        <option value="">選択してください</option>
                        <option value="50万円未満">50万円未満</option>
                        <option value="50-100万円">50-100万円</option>
                        <option value="100-300万円">100-300万円</option>
                        <option value="300-500万円">300-500万円</option>
                        <option value="500万円以上">500万円以上</option>
                        <option value="要相談">要相談</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      件名
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                      placeholder="件名を入力してください"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      メッセージ <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">（最大500文字）</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                      placeholder="お問い合わせ内容を詳しくお書きください"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.message.length}/500文字
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-line animate-spin mr-2"></i>
                          送信中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          送信する
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}

          {/* 求人応募フォーム */}
          {activeTab === 'recruitment' && (
            <>
              {recruitSubmitMessage && (
                <div
                  className={`mb-6 md:mb-8 p-4 rounded-lg text-sm md:text-base ${
                    recruitSubmitMessage.includes('ありがとう')
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`}
                >
                  {recruitSubmitMessage}
                </div>
              )}

              <div className="bg-white rounded-xl shadow-lg p-5 md:p-8">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-briefcase-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900">求人応募フォーム</h3>
                    <p className="text-xs md:text-sm text-gray-500">ご応募お待ちしております</p>
                  </div>
                </div>

                <form
                  id="recruitment-form"
                  onSubmit={handleRecruitSubmit}
                  className="space-y-5 md:space-y-6"
                  data-readdy-form
                >
                  {/* 名前・メール */}
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div>
                      <label htmlFor="recruit-name" className="block text-sm font-medium text-gray-700 mb-2">
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="recruit-name"
                        name="name"
                        value={recruitFormData.name}
                        onChange={handleRecruitInputChange}
                        required
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                        placeholder="お名前を入力してください"
                      />
                    </div>
                    <div>
                      <label htmlFor="recruit-email" className="block text-sm font-medium text-gray-700 mb-2">
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="recruit-email"
                        name="email"
                        value={recruitFormData.email}
                        onChange={handleRecruitInputChange}
                        required
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                        placeholder="メールアドレスを入力してください"
                      />
                    </div>
                  </div>

                  {/* 電話・年齢 */}
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div>
                      <label htmlFor="recruit-phone" className="block text-sm font-medium text-gray-700 mb-2">
                        電話番号
                      </label>
                      <input
                        type="tel"
                        id="recruit-phone"
                        name="phone"
                        value={recruitFormData.phone}
                        onChange={handleRecruitInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                        placeholder="電話番号を入力してください"
                      />
                    </div>
                    <div>
                      <label htmlFor="recruit-age" className="block text-sm font-medium text-gray-700 mb-2">
                        年齢
                      </label>
                      <input
                        type="text"
                        id="recruit-age"
                        name="age"
                        value={recruitFormData.age}
                        onChange={handleRecruitInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                        placeholder="例：28"
                      />
                    </div>
                  </div>

                  {/* 希望職種・経験年数 */}
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div>
                      <label htmlFor="recruit-position" className="block text-sm font-medium text-gray-700 mb-2">
                        希望職種 <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="recruit-position"
                        name="position"
                        value={recruitFormData.position}
                        onChange={handleRecruitInputChange}
                        required
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base pr-8"
                      >
                        <option value="">選択してください</option>
                        <option value="エンジニア">エンジニア</option>
                        <option value="デザイナー">デザイナー</option>
                        <option value="営業">営業</option>
                        <option value="企画・マーケティング">企画・マーケティング</option>
                        <option value="映像制作">映像制作</option>
                        <option value="カスタマーサポート">カスタマーサポート</option>
                        <option value="その他">その他</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="recruit-experience" className="block text-sm font-medium text-gray-700 mb-2">
                        経験年数
                      </label>
                      <select
                        id="recruit-experience"
                        name="experience"
                        value={recruitFormData.experience}
                        onChange={handleRecruitInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base pr-8"
                      >
                        <option value="">選択してください</option>
                        <option value="未経験">未経験</option>
                        <option value="1年未満">1年未満</option>
                        <option value="1-3年">1〜3年</option>
                        <option value="3-5年">3〜5年</option>
                        <option value="5-10年">5〜10年</option>
                        <option value="10年以上">10年以上</option>
                      </select>
                    </div>
                  </div>

                  {/* 現在の職業・入社可能日 */}
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <div>
                      <label htmlFor="recruit-currentJob" className="block text-sm font-medium text-gray-700 mb-2">
                        現在の職業
                      </label>
                      <input
                        type="text"
                        id="recruit-currentJob"
                        name="currentJob"
                        value={recruitFormData.currentJob}
                        onChange={handleRecruitInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                        placeholder="例：ソフトウェアエンジニア"
                      />
                    </div>
                    <div>
                      <label htmlFor="recruit-availableDate" className="block text-sm font-medium text-gray-700 mb-2">
                        入社可能日
                      </label>
                      <input
                        type="text"
                        id="recruit-availableDate"
                        name="availableDate"
                        value={recruitFormData.availableDate}
                        onChange={handleRecruitInputChange}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                        placeholder="例：即日、2025年4月〜"
                      />
                    </div>
                  </div>

                  {/* 希望年収 */}
                  <div>
                    <label htmlFor="recruit-salary" className="block text-sm font-medium text-gray-700 mb-2">
                      希望年収
                    </label>
                    <select
                      id="recruit-salary"
                      name="salary"
                      value={recruitFormData.salary}
                      onChange={handleRecruitInputChange}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base pr-8"
                    >
                      <option value="">選択してください</option>
                      <option value="300万円未満">300万円未満</option>
                      <option value="300-400万円">300〜400万円</option>
                      <option value="400-500万円">400〜500万円</option>
                      <option value="500-600万円">500〜600万円</option>
                      <option value="600-800万円">600〜800万円</option>
                      <option value="800万円以上">800万円以上</option>
                      <option value="応相談">応相談</option>
                    </select>
                  </div>

                  {/* 志望動機 */}
                  <div>
                    <label htmlFor="recruit-motivation" className="block text-sm font-medium text-gray-700 mb-2">
                      志望動機 <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">（最大500文字）</span>
                    </label>
                    <textarea
                      id="recruit-motivation"
                      name="motivation"
                      rows={5}
                      value={recruitFormData.motivation}
                      onChange={handleRecruitInputChange}
                      required
                      maxLength={500}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                      placeholder="志望動機をお書きください"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {recruitFormData.motivation.length}/500文字
                    </div>
                  </div>

                  {/* 自己PR */}
                  <div>
                    <label htmlFor="recruit-selfPR" className="block text-sm font-medium text-gray-700 mb-2">
                      自己PR
                      <span className="text-xs text-gray-500 ml-2">（最大500文字）</span>
                    </label>
                    <textarea
                      id="recruit-selfPR"
                      name="selfPR"
                      rows={5}
                      value={recruitFormData.selfPR}
                      onChange={handleRecruitInputChange}
                      maxLength={500}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
                      placeholder="スキルや経験、アピールポイントをお書きください"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {recruitFormData.selfPR.length}/500文字
                    </div>
                  </div>

                  {/* 送信ボタン */}
                  <div className="text-center">
                    <button
                      type="submit"
                      disabled={isRecruitSubmitting}
                      className="bg-teal-600 hover:bg-teal-700 text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRecruitSubmitting ? (
                        <>
                          <i className="ri-loader-line animate-spin mr-2"></i>
                          送信中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          応募する
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">アクセス</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              新宿駅から徒歩5分の好立地です
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="bg-gray-50 rounded-xl p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">VES株式会社</h3>
                <div className="space-y-3 md:space-y-4 text-sm md:text-base">
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-blue-600 mr-3 mt-1 flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold">住所</p>
                      <p className="text-gray-600">
                        〒160-0022<br />
                        東京都新宿区新宿1-34-14<br />
                        第二貝塚ビル101
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-train-line text-blue-600 mr-3 mt-1 flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold">アクセス</p>
                      <p className="text-gray-600">
                        JR新宿駅 東口より徒歩5分<br />
                        東京メトロ丸ノ内線 新宿御苑前駅より徒歩3分
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-time-line text-blue-600 mr-3 mt-1 flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold">営業時間</p>
                      <p className="text-gray-600">
                        平日 10:00-18:00<br />
                        土日祝日は休業
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3240.6967543567434!2d139.70247831525326!3d35.688651880189936!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188cd0d2e1a5a7%3A0x1c32a1f1ecb2c4a5!2z5paw5a6_77yR5LiB55uu77yT77yU4oiS77yR77yU!5e0!3m2!1sja!2sjp!4v1647321234567!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="VES株式会社 所在地"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6 md:gap-8">
            <div className="md:col-span-2">
              <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">AI Code グループ</h3>
              <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6 leading-relaxed">
                AIとホログラムで、未来のコミュニケーションをデザインする企業です。
                革新的な技術で、より豊かな未来を創造していきます。
              </p>
            </div>

            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4">サービス</h4>
              <ul className="space-y-2 text-sm md:text-base text-gray-300">
                <li><a href="#" className="hover:text-white cursor-pointer">パペロボ</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">ホログラム放送局</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">スタジオレンタル</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">AIソリューション</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4">クイックリンク</h4>
              <ul className="space-y-2 text-sm md:text-base text-gray-300">
                <li><button onClick={() => navigate('/')} className="hover:text-white cursor-pointer">ホーム</button></li>
                <li><button onClick={() => navigate('/media')} className="hover:text-white cursor-pointer">メディア</button></li>
                <li><span className="text-white">お問い合わせ</span></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 md:mt-12 pt-6 md:pt-8 text-center text-gray-400 text-xs md:text-sm">
            <p>&copy; 2024 VES株式会社. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
